import styles from '../opm-summary-waterfall-erf.module.scss';
import { SelectOption } from '@app/shared/models/option';
import { FormProvider, useForm } from 'react-hook-form';
import { formConfigBase } from '@app/shared/constants/form-config-base';
import { Navigate, generatePath } from 'react-router-dom';
import { RouteConstants } from '@app/modules/projects/RouteConstants';
import { dateFormatter } from '@app/shared/formatters';
import {
  setOpmWaterfallFirstLastTableSelectedYear,
  setOpmWaterfallSelectedTab,
} from '@core/store/ui-values-slice';
import { OpmFirstLastWaterfallDictionaryDto } from '@app/shared/models/contracts/opm-calculation-results-dto';
import useSetSelectedTab from '@app/core/hooks/useSetSelectedTab';
import InstrumentCalculationsErf from './instrument-calculations-erf/InstrumentCalculationsErf';
import useInstrumentSort from '@app/shared/hooks/useInstrumentSort';
import SummaryCalculationsErf from './summary-calculations-erf/SummaryCalculationsErf';
import SummaryCalculationsHeader from './summary-calculations-erf/SummaryCalculationsHeader';
import { useAppDispatch, useAppSelector } from '@app/core/hooks/redux-hooks';
import { enumKeyByValue } from '@app/shared/helpers';
import { OpmSimulatedValue } from '@app/shared/models/contracts/enums/shared-enums';

const FirstLastTableErf = (): JSX.Element => {
  useSetSelectedTab(RouteConstants.FirstLastSimulations, setOpmWaterfallSelectedTab);
  const dispatch = useAppDispatch();
  const opmCalc = useAppSelector((state) => state.opmCalculation);
  const isEquitySimulation =
    opmCalc?.riskFreeValues?.inputs?.opmSimulatedValue ===
    enumKeyByValue(OpmSimulatedValue, OpmSimulatedValue.Equity);
  const results = useAppSelector((state) => state.opmCalculation.riskFreeValues);
  const opmWaterfall = results?.waterfalls;
  const storedYear = useAppSelector(
    (state) => state.uiValues.userSelections.opmWaterfall.firstLastTable.selectedYear
  );
  const storedInstrument = useAppSelector(
    (state) => state.uiValues.userSelections.opmWaterfall.firstLastTable.selectedInstrument
  );
  const waterfallIterations = useAppSelector((state) => state.uiValues.opmWaterfallIterations ?? 5);

  let instrumentOptions: SelectOption[] = [];
  let yearOptions: SelectOption[] = [];

  const getFirstKey = (obj: OpmFirstLastWaterfallDictionaryDto | undefined): string | undefined => {
    if (!obj || Object.keys(obj).length === 0) {
      return undefined;
    }
    return Object.keys(obj)[0];
  };

  const firstKey = getFirstKey(opmWaterfall);
  const instruments = (firstKey && opmWaterfall[firstKey]?.first?.[1]?.instruments) ?? {};
  const waterfallInstruments = Object.entries(instruments).map(
    ([instrumentId, instrumentData]) => ({
      instrumentId,
      ...instrumentData,
    })
  );
  const sortedWaterfallInstruments = useInstrumentSort(waterfallInstruments);

  if (opmWaterfall && sortedWaterfallInstruments.length > 0) {
    instrumentOptions = sortedWaterfallInstruments
      .filter((instrument) => instrument.shouldBeValued)
      .map((instrument) => ({
        value: instrument.instrumentId,
        viewValue: instrument.instrumentName,
      }));

    yearOptions = Object.keys(opmWaterfall).map((key) => ({
      value: key,
      viewValue: dateFormatter(key, { month: 'short', year: 'numeric' }),
    }));
  }
  const isStoredInstrumentValid =
    storedInstrument && instrumentOptions.some((option) => option.value === storedInstrument);
  const isStoredYearValid = storedYear && yearOptions.some((option) => option.value === storedYear);

  const defaultInstrument = isStoredInstrumentValid
    ? storedInstrument
    : String(instrumentOptions[0]?.value);
  const defaultYear = isStoredYearValid ? storedYear : String(yearOptions[0]?.value);

  const formMethods = useForm({
    ...formConfigBase,
    defaultValues: {
      instrumentSelect: defaultInstrument,
      yearSelect: defaultYear,
    },
  });

  const { setValue, watch } = formMethods;
  const selectedYear = watch('yearSelect');

  if (!results || Object.keys(results).length === 0) {
    const newPath = generatePath(`../../${RouteConstants.ProjectDetails}`);
    return <Navigate to={newPath} />;
  }

  const titleWidth = '300px';
  const cellWidth = '100px';

  // measure taken to prevent the table from breaking if selectedYear
  // is removed, and the user re-runs the calc whilst viewing the table
  if (!yearOptions.find((y) => y.value === selectedYear)) {
    setValue('yearSelect', String(yearOptions[0].value));
  }

  return (
    <div className="main-container-padding-remove">
      <FormProvider {...formMethods}>
        <table
          className={`table-primary table-primary--zebra table-primary--framed table-primary--title-column-nowrap ${styles['opm-top-bottom-waterfall-table']}`}
          data-testid="opm--top-bottom-waterfall-table">
          <colgroup>
            <col style={{ width: titleWidth }} />
            {new Array(waterfallIterations).map((_, index) => (
              <col key={index} style={{ width: cellWidth }} />
            ))}
          </colgroup>
          <SummaryCalculationsHeader
            opmWaterfall={opmWaterfall}
            waterfallIterations={waterfallIterations}
            selectedYear={selectedYear}
            yearOptions={yearOptions}
            onYearChange={(val) => {
              dispatch(setOpmWaterfallFirstLastTableSelectedYear(val));
              setValue('yearSelect', val);
            }}
          />
          <tbody>
            <SummaryCalculationsErf
              opmWaterfall={opmWaterfall}
              selectedYear={
                selectedYear === undefined ? String(yearOptions[0]?.value) : selectedYear
              }
              isEquitySimulation={isEquitySimulation}
            />
            <InstrumentCalculationsErf
              opmWaterfall={opmWaterfall}
              selectedYear={
                selectedYear === undefined ? String(yearOptions[0]?.value) : selectedYear
              }
            />
          </tbody>
        </table>
      </FormProvider>
    </div>
  );
};

export default FirstLastTableErf;
